import type {ButtonProps} from '@primer/react'
import {Button, IconButton} from '@primer/react'
import {type Icon, XIcon} from '@primer/octicons-react'
import type {PropsWithChildren, MouseEventHandler} from 'react'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import styles from './GrowthBanner.module.css'

export interface GrowthBannerProps {
  /**
   * A callback which is called whenever the 'x' close button is clicked
   */
  closeButtonClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Additional CSS class name
   */
  className?: string
  /**
   * A leading `@primer/octicons-react` Icon.
   */
  icon?: Icon
  /**
   * When defined, renders a primary button. You may pass any Primer Button props to this object.
   * To set Button text content, pass in a text value to the `children` prop
   */
  primaryButtonProps?: ButtonProps
  /**
   * When defined, renders a secondary button. You may pass any Primer Button props to this object.
   * To set Button text content, pass in a text value to the `children` prop
   */
  secondaryButtonProps?: ButtonProps
  /**
   * Hides the close button when `false`
   * @default true
   */
  showCloseButton?: boolean
  /**
   * GrowthBanner title
   */
  title: string
  /**
   * GrowthBanner variants will modify the icon colors
   */
  variant?: 'default' | 'information' | 'warning' | 'danger' | 'success' | 'done'
}

const VARIANT_CLASSES: Record<string, string> = {
  default: styles.iconCircleDefault,
  information: styles.iconCircleInformation,
  warning: styles.iconCircleWarning,
  danger: styles.iconCircleDanger,
  success: styles.iconCircleSuccess,
  done: styles.iconCircleDone,
}

export function GrowthBanner({
  children,
  className,
  closeButtonClick,
  icon,
  primaryButtonProps,
  secondaryButtonProps,
  showCloseButton = true,
  title,
  variant = 'default',
  ...props
}: PropsWithChildren<GrowthBannerProps>) {
  const IconComponent = icon
  const iconVisible = !!icon
  return (
    <div className={clsx(styles.banner, className)} {...props}>
      <div className={styles.innerRow}>
        {iconVisible && IconComponent && (
          <div
            {...testIdProps('growth-GrowthBanner-icon')}
            className={clsx(styles.iconCircle, VARIANT_CLASSES[variant])}
          >
            <IconComponent size={16} />
          </div>
        )}
        <div
          className={clsx(styles.textWrapper, iconVisible ? styles.textWrapperWithIcon : styles.textWrapperWithoutIcon)}
        >
          <p className={styles.titleText}>{title}</p>
          <span className={styles.childrenText}>{children}</span>
        </div>
      </div>
      <div
        className={clsx(
          styles.actionsContainer,
          iconVisible ? styles.actionsContainerWithIcon : styles.actionsContainerWithoutIcon,
        )}
      >
        {secondaryButtonProps && (
          <Button variant="invisible" className={styles.actionButton} {...secondaryButtonProps} />
        )}
        {primaryButtonProps && <Button className={styles.actionButton} {...primaryButtonProps} />}
        {showCloseButton && (
          // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
          <IconButton
            unsafeDisableTooltip
            variant="invisible"
            /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
            aria-label="Close GrowthBanner"
            onClick={closeButtonClick}
            icon={XIcon}
            size="medium"
            className={styles.closeButton}
          />
        )}
      </div>
    </div>
  )
}
